import { useEffect, useCallback } from 'react'
import type { RefObject } from 'react'
import type MapboxDraw from '@mapbox/mapbox-gl-draw'
// @ts-expect-error - turf types issue
import * as turf from '@turf/turf'
import {
  convertFromGeoJSON,
  convertToGeoJSON,
  ensurePolygonClosed,
} from '../../../utils/mapHelpers'

interface UseEditSaveCancelParams {
  editingZoneId: string | null
  editMode: 'polygon' | 'grid' | null
  gridCellPosition: [number, number] | null
  gridSize: number
  generateGridCell: (center: [number, number], size: number) => GeoJSON.Feature
  getZoneById: (id: string) =>
    | {
        coordinates?: number[][]
      }
    | undefined
  drawRef: RefObject<MapboxDraw | null>
  setDragPanEnabled: React.Dispatch<React.SetStateAction<boolean>>
  lastCalculatedDistanceRef?: React.MutableRefObject<number>
}

export function useEditSaveCancel({
  editingZoneId,
  editMode,
  gridCellPosition,
  gridSize,
  generateGridCell,
  getZoneById,
  drawRef,
  setDragPanEnabled,
}: UseEditSaveCancelParams) {
  const handleGridSave = useCallback(() => {
    if (!gridCellPosition) {
      // eslint-disable-next-line no-console
      console.error('❌ Grid cell position not available')
      return null
    }
    const gridCell = generateGridCell(gridCellPosition, gridSize)
    if (gridCell.geometry.type !== 'Polygon') return null

    const coordinates = gridCell.geometry.coordinates[0] ?? []
    const frontendCoords = ensurePolygonClosed(convertFromGeoJSON(coordinates))
    const area = turf.area(turf.polygon(gridCell.geometry.coordinates))

    return { coordinates: frontendCoords, area: Math.round(area) }
  }, [gridCellPosition, gridSize, generateGridCell])

  const handlePolygonSave = useCallback(() => {
    if (!drawRef.current) {
      // eslint-disable-next-line no-console
      console.error('❌ Draw control not available')
      return null
    }

    const draw = drawRef.current
    const editingFeature = draw
      .getAll()
      .features.find(
        f =>
          (f.properties as { zoneId?: string })?.zoneId === editingZoneId ||
          f.id === editingZoneId
      )

    if (editingFeature?.geometry.type !== 'Polygon') {
      const zone = editingZoneId ? getZoneById(editingZoneId) : null
      if (!zone?.coordinates?.length) {
        // eslint-disable-next-line no-console
        console.error('❌ Zone not found in Draw or Redux')
        return null
      }
      const area = turf.area(turf.polygon([convertToGeoJSON(zone.coordinates)]))
      return { coordinates: zone.coordinates, area: Math.round(area) }
    }

    const coords = editingFeature.geometry.coordinates[0] ?? []
    if (coords.length === 0) {
      // eslint-disable-next-line no-console
      console.error('❌ Feature has no coordinates')
      return null
    }

    const frontendCoords = ensurePolygonClosed(convertFromGeoJSON(coords))
    const area = turf.area(turf.polygon([convertToGeoJSON(frontendCoords)]))

    return { coordinates: frontendCoords, area: Math.round(area) }
  }, [drawRef, editingZoneId, getZoneById])

  useEffect(() => {
    window._drawControlSaveEdit = async () => {
      if (!editingZoneId) {
        // eslint-disable-next-line no-console
        console.warn('⚠️ Cannot save: editingZoneId is missing')
        return null
      }
      return editMode === 'grid' ? handleGridSave() : handlePolygonSave()
    }

    window._drawControlCancelEdit = () => {
      if (drawRef.current) {
        const draw = drawRef.current
        draw.getAll().features.forEach(f => f.id && draw.delete(f.id as string))
        setDragPanEnabled(true)
      }
    }

    return () => {
      delete window._drawControlSaveEdit
      delete window._drawControlCancelEdit
    }
  }, [
    editingZoneId,
    editMode,
    handleGridSave,
    handlePolygonSave,
    drawRef,
    setDragPanEnabled,
  ])
}
